import { ChangeDetectionStrategy, Component, computed, signal, inject, input, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, of, switchMap } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MeasurementsService, DevicesService, StagesService, IngestedCsvService } from '@cosmos/angular/api';
import {
  Measurement, Project, Device, User, SHM_VARIABLES, DATAPOINT_TRACE_ALWAYS_VISIBLE,
  thickLossStructuralLimit,
  // TODO(researchers): thickLossAtIcorrCrossover, ICORR_CROSSOVER_THRESHOLD — for the orange band.
} from '@cosmos/shared';
import { ErrorService, MeasurementRefreshService, PanelService, RBACService, StateService } from '@cosmos/angular/utils';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { MultiLineChartComponent, DatapointClickEvent } from '../../data/charts/multi-line-chart/multi-line-chart.component';
import { ThresholdBandConfig } from '../../data/charts/plugins/chartjs-plugin-threshold-bands';
import { SHM_ICORR_THRESHOLD_BANDS } from './shm-threshold-bands';
import { SHM_TEMP_SLOTS, ShmTempLabels, mergeChannelTemps, shmTemperatureTitle } from './shm-series';
import { SHMDatapointAdapter } from '../datapoint/adapters/shm-datapoint.adapter';
import { DatapointDetailsComponent } from '../datapoint/datapoint-details/datapoint-details.component';
import { ShmRescheduleDialogComponent } from '../shm-reschedule-dialog/shm-reschedule-dialog.component';
import { DataTraceDialogComponent } from '../data-trace-dialog/data-trace-dialog.component';
import { RawDataViewerComponent } from '../raw-data-viewer/raw-data-viewer.component';
import { EditMeasurementForm, MeasurementForm } from '../../dynamic-form/forms/measurement.form';
import { DynamicFormDialog } from '../../dynamic-form/dialog-form/dialog-form.component';
import { MeasurementActionsMenuComponent, MeasurementAction } from '../../components/measurement-actions-menu/measurement-actions-menu.component';
import { MeasurementInfoCardComponent } from '../../components/measurement-info-card/measurement-info-card.component';
import { MeasurementChangeStageDialogComponent } from '../../components/measurement-change-stage-dialog/measurement-change-stage-dialog.component';
import { BreadcumbsService } from '../../navigation/breadcrumbs/breadcrumbs.service';
import { MonitoringTreeService } from '../../monitoring/monitoring-tree.service';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleFilterComponent, ToggleFilterOption } from '../../components/toggle-filter/toggle-filter.component';

@Component({
  selector: 'cosmos-structural-health-measurement',
  imports: [
    TranslatePipe, NgTemplateOutlet, ToggleFilterComponent,
    CardModule, DividerModule, ButtonModule, ConfirmDialogModule,
    MultiLineChartComponent, DynamicDialogModule, ProgressSpinnerModule,
    MeasurementActionsMenuComponent, MeasurementInfoCardComponent,
  ],
  templateUrl: './structural-health-measurement.component.html',
  providers: [DialogService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StructuralHealthMeasurementComponent {

  measurementId = input<string>();

  // Signals — mutations trigger OnPush re-render
  readonly measurement = signal<Measurement | null>(null);
  readonly project = signal<Project | null>(null);
  readonly device = signal<Device | null>(null);
  user: User;

  /** One entry per configured (non-'None') channel — the unfiltered source for every chart. */
  private readonly channelObjects = signal<any[]>([]);

  /**
   * Global channel selection (channel keys), shared by every chart. Rendered twice —
   * top of page and above the temperature charts — both bound here. Never empty
   * (ToggleFilterComponent locks the last channel on).
   */
  readonly channelSelection = signal<string[]>([]);
  readonly channelOptions = computed<ToggleFilterOption[]>(() =>
    this.channelObjects().map((c) => ({ value: c.channelKey as string, label: c.name as string })));

  private readonly filteredChannels = computed(() => {
    const sel = this.channelSelection();
    return this.channelObjects().filter((c) => sel.includes(c.channelKey));
  });

  readonly corrosionData = computed(() => this.filteredChannels().filter((c) => c.type === 'corrosion'));
  /** ecorr runs on its own time axis (`time_ecorr`); falls back to `time` for older data. */
  readonly corrosionEcorrData = computed(() =>
    this.corrosionData().map((c) => ({ ...c, time: c.time_ecorr ?? c.time })));
  readonly phData = computed(() => this.filteredChannels().filter((c) => c.type === 'ph'));
  readonly chloridesData = computed(() => this.filteredChannels().filter((c) => c.type === 'chlorides'));

  /**
   * Temperature charts. The board has two NTC probes (+ a PCB sensor), read on every
   * electrochemical capture and stored per channel with that channel's `time`. Each
   * chart is the concatenation of that probe across all SELECTED channels, in one
   * time-ordered series. `channelKeys`/`srcIndex` map each merged point back to its
   * source channel so a click can open the right datapoint.
   */
  readonly temperatureGroups = computed(() => {
    const labels = this.measurement()?.['tempLabels'] as ShmTempLabels | undefined;
    const channels = this.filteredChannels().filter((c) => c.type === 'corrosion');
    return SHM_TEMP_SLOTS.flatMap((slot) => {
      const merged = mergeChannelTemps(channels, slot);
      if (!merged) return [];
      const title = shmTemperatureTitle(slot, labels, (k) => this.translateService.instant(k));
      return [{
        title,
        chartId: `shm_${slot}`,
        dataObjects: [{
          name: title,
          time: merged.time,
          temperature: merged.values,
          channelKeys: merged.channelKeys,
          slot,
          srcIndex: merged.srcIndex,
        }],
      }];
    });
  });

  readonly loadingMetadata = signal(false);
  readonly loadingData = signal(false);

  readonly corrosionRange = signal<{ min: number; max: number } | null>(null);
  readonly phRange = signal<{ min: number; max: number } | null>(null);
  readonly chloridesRange = signal<{ min: number; max: number } | null>(null);
  readonly temperatureRange = signal<{ min: number; max: number } | null>(null);

  // `charge` is still computed and stored, but its cumulative curve isn't meaningful to users.
  readonly corrosionVariables = Object.keys(SHM_VARIABLES.corrosion).filter((v) => v !== 'charge');
  readonly phVariables = Object.keys(SHM_VARIABLES.ph);
  readonly chloridesVariables = Object.keys(SHM_VARIABLES.chlorides);

  readonly measurementsService = inject(MeasurementsService);
  readonly devicesService = inject(DevicesService);
  private readonly stagesService = inject(StagesService);
  readonly errorService = inject(ErrorService);
  readonly translateService = inject(TranslateService);
  readonly dialogService = inject(DialogService);
  readonly confirmationService = inject(ConfirmationService);
  readonly rbac = inject(RBACService);
  readonly stateService = inject(StateService);
  private readonly refreshService = inject(MeasurementRefreshService);
  private readonly breadcumbsService = inject(BreadcumbsService);
  private readonly treeService = inject(MonitoringTreeService);
  private readonly panelService = inject(PanelService);
  private readonly ingestedCsvService = inject(IngestedCsvService);

  /** _id -> { filename, status } for the datapoint panel's source-file card.
   *  Populated once per data load; non-fatal if the fetch fails. */
  private readonly csvRows = signal<Map<string, { filename?: string; status?: string }>>(new Map());

  constructor() {
    this.stateService.currentUser$
      .pipe(takeUntilDestroyed())
      .subscribe(x => { this.user = x; });

    effect(() => {
      if (this.measurementId()) {
        this.getData();
      }
    });
  }

  getData() {
    this.loadingMetadata.set(true);
    this.loadingData.set(true);

    // Request 1: Metadata for the card
    const metadataQuery = '/?select=name,description,active,createdAt,updatedAt,measurementType,project,device,stage&populate_project&populate_device=name';
    this.measurementsService.findById(this.measurementId(), metadataQuery).subscribe({
      next: (response) => {
        if (response.body) {
          if (this.loadingData()) {
            this.measurement.set(response.body);
            this.project.set(response.body.project);
            this.device.set(response.body.device as unknown as Device);
          }
        }
        this.loadingMetadata.set(false);
      },
      error: (e) => {
        this.loadingMetadata.set(false);
        this.errorService.handleError(e);
      }
    });

    // Request 2: Full data for charts and editing
    this.measurementsService.findById(this.measurementId(), '/?populate_project&populate_device=name').subscribe({
      next: (response) => {
        if (response.body) {
          this.measurement.set(response.body);
          this.project.set(response.body.project);
          this.device.set(response.body.device as unknown as Device);
          this.prepareChartData();
        }
        this.loadingData.set(false);
      },
      error: (e) => {
        this.loadingData.set(false);
        this.errorService.handleError(e);
      }
    });

    // Request 3: ingested-CSV list — used to name/annotate the datapoint panel's
    // source-file card. Non-fatal; the card falls back to the raw id.
    this.ingestedCsvService.getForMeasurement(this.measurementId()).subscribe({
      next: (res) => {
        const map = new Map<string, { filename?: string; status?: string }>();
        (res.body ?? []).forEach((r) => map.set(r._id, { filename: r.filename, status: r.status }));
        this.csvRows.set(map);
      },
      error: () => { /* non-fatal */ }
    });
  }

  /**
   * `dataChannels` is stored as a map keyed `channel1`, `channel2`, … (matching
   * `channelData`'s keys). A stale edit save could persist it as the Formly
   * field-array shape ([{ channelId: 1, type, parameters }]); fold that back to
   * the map so `channelData[key]` lookups still resolve. Already-a-map (or
   * absent) passes straight through.
   */
  private normalizeDataChannels(raw: unknown): Record<string, any> {
    if (Array.isArray(raw)) {
      return raw.reduce((acc: Record<string, any>, c: any) => {
        if (c?.channelId != null) {
          acc[`channel${c.channelId}`] = { type: c.type, parameters: c.parameters, name: c.name };
        }
        return acc;
      }, {});
    }
    return (raw as Record<string, any>) ?? {};
  }

  prepareChartData() {
    const m = this.measurement();
    if (!m || !m.time) return;

    const time = m.time;
    const channelData = m['channelData'] || {};
    const dataChannels = this.normalizeDataChannels(m['dataChannels']);

    // Unfiltered per-channel objects; the corrosion/ph/chlorides/temperature
    // views are computed from these + the global channel filter.
    const channels: any[] = [];

    Object.keys(dataChannels).forEach(channelId => {
      const config = dataChannels[channelId];
      if (!config || config.type === 'None') return;

      const channelValues = channelData[channelId] || {};
      // User-set channel name wins; otherwise fall back to "Channel <n>"
      // (channelId is the map key, e.g. "channel1").
      const channelNum = channelId.replace('channel', '');
      const displayName = config.name?.trim()
        || `${this.translateService.instant('measurement.shm.channel')} ${channelNum}`;
      channels.push({
        name: displayName,
        channelKey: channelId,           // stable key for the datapoint adapter
        type: config.type,
        time: time,
        ...channelValues
      });
    });

    // Keep the user's selection across reloads: everything stays selected if it was
    // (so newly added channels show up), otherwise drop channels that no longer exist.
    const prevKeys = this.channelObjects().map((c) => c.channelKey as string);
    const prevSel = this.channelSelection();
    const wasAll = prevKeys.length === 0 || prevKeys.every((k) => prevSel.includes(k));
    this.channelObjects.set(channels);
    const keys = channels.map((c) => c.channelKey as string);
    const kept = prevSel.filter((k) => keys.includes(k));
    this.channelSelection.set(wasAll || !kept.length ? keys : kept);
  }

  downloadCSV() {
    const query = `_id=${this.measurementId()}`;
    this.measurementsService.downloadCSV(query).subscribe({
      next: (response) => {
        const blob = new Blob([response.body], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `SHM_${this.measurement().name}_${new Date().getTime()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  editMeasurement() {
    const m = this.measurement();
    // The channel geometry selects (bar diameter / length) are sourced from the
    // device's DeviceProfile — the measurement's populated `device` only carries
    // `name`, so fetch the profile separately, the same way the create flow does.
    const device = m.device as unknown as (Device | string);
    const deviceId = typeof device === 'string' ? device : device?._id;
    this.devicesService.findById(deviceId, '?populate_deviceProfile=class,parameters').subscribe({
      next: (deviceRes) => this.openEditMeasurementDialog(m, deviceRes.body?.deviceProfile),
      error: (e) => this.errorService.handleError(e),
    });
  }

  private openEditMeasurementDialog(m: Measurement, deviceProfile: any) {
    const editMeasurementForm = new EditMeasurementForm(
      this.translateService, m, this.user,
      { visualizationConfig: this.project().visualizationConfig, measurementType: m.measurementType, deviceProfile }
    );
    const dialogRef = this.dialogService.open(DynamicFormDialog, {
      data: { form: editMeasurementForm, dialogTitle: this.translateService.instant('dialog.title.edit.measurement') },
      header: this.translateService.instant('dialog.title.edit.measurement'),
      modal: true, closable: true, width: '50vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });

    dialogRef.onClose.subscribe(result => {
      if (result !== undefined) {
        // EditMeasurementForm expands `dataChannels` from the stored map
        // ({channel1: {...}}) into a Formly field-array ([{channelId, ...}]).
        // It must be folded back to a map before saving, or `channelData`
        // (still keyed channel1/channel2) no longer lines up with it and every
        // chart on the detail page renders empty. f-admin's edit path does the
        // same via transformArrayToMap.
        this.measurementsService.update([m._id, MeasurementForm.transformArrayToMap(result)]).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.updateSuccess',
            onSuccess: () => this.getData(),
          }),
        );
      }
    });
  }

  finishMeasurement(event: Event) {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translateService.instant('measurement.finish-dialog.message'),
      icon: 'fa-solid fa-warning',
      header: this.translateService.instant('dialog.confirm.warning'),
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: this.translateService.instant('button.finish')
      },
      accept: () => {
        this.measurementsService.finish(this.measurementId()).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.finishSuccess',
            onSuccess: () => {
              this.getData();
              this.refreshService.trigger();
            },
          }),
        );
      }
    });
  }

  moveStage(measurement: Measurement) {
    this.fetchProjectStageTree(this.project()._id).subscribe({
      next: (stageTree) => {
        const dialogRef = this.dialogService.open(MeasurementChangeStageDialogComponent, {
          data: { measurement, stageTree },
          header: this.translateService.instant('measurement.move_stage.title'),
          modal: true, closable: true, width: '30vw',
          breakpoints: { '960px': '75vw', '640px': '90vw' },
        });
        dialogRef.onClose.subscribe((result: { stage: string } | undefined) => {
          if (result !== undefined) {
            this.measurementsService.changeStage(measurement._id, result).subscribe(
              this.errorService.handleMutation({
                successMessageKey: 'measurement.messages.updateSuccess',
                onSuccess: () => {
                  // A stage-only move doesn't change the flat
                  // /monitoring/measurement/:id URL, so nothing re-fetches or
                  // re-resolves on its own — trigger the parent page fetch,
                  // refresh the tree rail (the leaf re-parents) and the (now
                  // stale) breadcrumb trail, and refetch this component's own
                  // measurement signal.
                  this.refreshService.trigger();
                  this.treeService.refresh();
                  this.breadcumbsService.refresh();
                  this.getData();
                },
              }),
            );
          }
        });
      },
      error: (e) => this.errorService.handleError(e),
    });
  }

  // Fetches every root stage in the project, then each root's full
  // descendant tree — the shape the change-stage dialog's tree picker needs.
  private fetchProjectStageTree(projectId: string) {
    return this.stagesService.find(`?project=${projectId}&rootOnly=true`).pipe(
      switchMap((rootsRes) => {
        const roots = (rootsRes.body as any[]) ?? [];
        if (roots.length === 0) return of([]);
        return this.stagesService.getForest(roots.map((r) => r._id)).pipe(map((res) => res.body ?? []));
      }),
    );
  }

  onCorrosionRangeChange(range: { min: number; max: number } | null) {
    this.corrosionRange.set(range);
  }

  // Thickloss (thickLoss, µm) risk-zone tones — reuse the icorr band colours.
  // TODO(i18n): `label`s are Spanish literals because the threshold-bands
  // plugin doesn't render them on-canvas yet; move to `| translate` keys once
  // it does.
  private static readonly THICKLOSS_ORANGE = 'rgba(249, 115, 22, 0.10)';
  private static readonly THICKLOSS_RED = 'rgba(239, 68, 68, 0.09)';

  /**
   * Rebar geometry + design strength feeding the thickloss threshold bands.
   *
   * TODO(researchers): with multiple corrosion channels the params can differ
   * per channel (sensor placement). This currently takes the first corrosion
   * channel; pending confirmation it may become "worst case across channels"
   * (lowest structural limit). Single swap point for that decision.
   */
  private resolveThickLossBandParams(
    m: Measurement | null,
  ): { cover?: number; diameter?: number; fck?: number } | null {
    const channels = this.normalizeDataChannels(m?.['dataChannels']);
    const corrosion = Object.values(channels).find((c) => c?.type === 'corrosion');
    if (!corrosion) return null;
    const p = corrosion.parameters ?? {};
    const diameter = p.diameter === 'custom' ? p.diameterCustom : p.diameter;
    return { cover: p.cover, diameter, fck: p.fck };
  }

  /**
   * Derived threshold bands for the thickloss chart. Reactive: recomputes when
   * `measurement()` changes (channel params, icorr series).
   *   - red ("Riesgo alto")     — from the RILEM structural limit upward.
   *   - orange ("Riesgo moderado") — TODO(researchers): from min(A, B, C_lim),
   *     where A = thickloss at first icorr ≥ ICORR_CROSSOVER_THRESHOLD, and
   *     B / C_lim are chlorides- / pH-derived limits whose pipelines don't
   *     exist yet. Left as `null` until specced.
   */
  readonly thickLossThresholdBands = computed<ThresholdBandConfig | null>(() => {
    const m = this.measurement();
    const params = this.resolveThickLossBandParams(m);
    if (!params) return null;

    const redFrom = thickLossStructuralLimit(params);

    // TODO(researchers): activate via
    //   const chData = (m?.['channelData'] ?? {})[<corrosion channel key>] ?? {};
    //   const a = thickLossAtIcorrCrossover(chData.icorr ?? [], chData.thickLoss ?? []);
    //   orangeFrom = minDefined(a, chloridesLimit, phLimit);
    let orangeFrom: number | null = null;

    // Severity bands are cumulative: orange (moderate) must sit below red
    // (high). If the crossover thickloss lands at or above the structural
    // limit, the moderate-risk zone is fully subsumed — draw red only.
    if (orangeFrom != null && redFrom != null && orangeFrom >= redFrom) {
      orangeFrom = null;
    }

    const bands: ThresholdBandConfig['bands'] = [
      { from: 0, color: 'rgba(0, 0, 0, 0)' }, // no risk — transparent
    ];
    if (orangeFrom != null) {
      bands.push({
        from: orangeFrom,
        color: StructuralHealthMeasurementComponent.THICKLOSS_ORANGE,
        label: 'Riesgo moderado',
      });
    }
    if (redFrom != null) {
      bands.push({
        from: redFrom,
        color: StructuralHealthMeasurementComponent.THICKLOSS_RED,
        label: 'Riesgo alto',
      });
    }
    if (bands.length === 1) return null; // nothing derivable yet

    return { bands, extendAxis: false };
  });

  thresholdBandsFor(variable: string): ThresholdBandConfig | null {
    if (variable === 'icorr') return SHM_ICORR_THRESHOLD_BANDS;
    if (variable === 'thickLoss') return this.thickLossThresholdBands();
    return null;
  }

  onPhRangeChange(range: { min: number; max: number } | null) {
    this.phRange.set(range);
  }

  onChloridesRangeChange(range: { min: number; max: number } | null) {
    this.chloridesRange.set(range);
  }

  onTemperatureRangeChange(range: { min: number; max: number } | null) {
    this.temperatureRange.set(range);
  }

  onDatapointClick(event: DatapointClickEvent, channelType: 'corrosion' | 'ph' | 'chlorides'): void {
    const src = event.sourceObject;
    if (!src?.channelKey) return;
    // NOTE: for ecorr the index is on `time_ecorr`; the adapter walks `time`, which
    // is identical until ecorr-specific handling is defined.
    this.openDatapoint(src.channelKey, channelType, event.index, event.variable);
  }

  /** Temperature points are merged across channels — resolve the clicked one back to its source channel/index. */
  onTemperatureClick(event: DatapointClickEvent): void {
    const src = event.sourceObject;
    const channelKey: string | undefined = src?.channelKeys?.[event.index];
    if (!channelKey) return;
    const type = this.channelObjects().find((c) => c.channelKey === channelKey)?.type ?? 'corrosion';
    this.openDatapoint(channelKey, type, src.srcIndex[event.index], src.slot);
  }

  private openDatapoint(
    channelKey: string,
    channelType: 'corrosion' | 'ph' | 'chlorides',
    index: number,
    highlightKey: string,
  ): void {
    const m = this.measurement();
    const channelData = (m?.['channelData'] ?? {})[channelKey];
    if (!channelData) return;

    const adapter = new SHMDatapointAdapter({
      measurementId: this.measurementId(),
      channelKey,
      channelData,
      channelType,
      startIndex: index,
      highlightKey,
      canDelete: this.rbac.isProjectAdmin(),
      csvLookup: (csvId: string) => this.csvRows().get(csvId),
      onDelete: ({ channelKey, ts }) =>
        this.measurementsService.removeDatapoint(this.measurementId(), {
          channelKey,
          ts: ts.toISOString(),
        }),
    });

    this.panelService.initialize(DatapointDetailsComponent, {
      adapter,
      onDeleted: () => this.getData(),
      downloadCsv: (csvId: string, filename?: string) => this.downloadSourceCsv(csvId, filename),
    });
  }

  private downloadSourceCsv(csvId: string, filename?: string): void {
    this.ingestedCsvService.download(this.measurementId(), csvId).subscribe({
      next: (res) => {
        const blob = res.body;
        if (!blob) return;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `${csvId}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (e) => this.errorService.handleError(e),
    });
  }

  // SHM currently exposes a subset of the tier-2 actions: change-device needs
  // SHM-specific constraints (deferred), and report generation is not yet
  // implemented for this type.
  readonly tier2Actions = computed<MeasurementAction[]>(() => {
    const actions: MeasurementAction[] = [
      { labelKey: 'measurement.download-csv', icon: 'fa-solid fa-download', action: () => this.downloadCSV() },
      { labelKey: 'measurement.rawDataViewer.action', icon: 'fa-solid fa-database', action: () => this.openRawDataViewer() },
      { labelKey: 'measurement.move_stage.action', icon: 'fa-solid fa-layer-group', action: () => this.moveStage(this.measurement()) },
    ];
    if (this.rbac.canEditProject()) {
      actions.push({ labelKey: 'measurement.reschedule.window.button', icon: 'fa-solid fa-crop', action: () => this.openRescheduleDialog() });
    }
    const m = this.measurement();
    const hasEdits = !!(m?.window?.startTs || m?.window?.endTs || (m?.removedPoints?.length ?? 0) > 0);
    if (DATAPOINT_TRACE_ALWAYS_VISIBLE || hasEdits) {
      actions.push({ labelKey: 'measurement.dataTrace.action', icon: 'fa-solid fa-clock-rotate-left', action: () => this.openDataTrace() });
    }
    return actions;
  });

  openRescheduleDialog(): void {
    const m = this.measurement();
    if (!m) return;

    // global min/max across all channels' channelDataAll (fallback to channelData / createdAt)
    const all = { ...(m['channelDataAll'] ?? m['channelData'] ?? {}) };
    const times: number[] = [];
    for (const ch of Object.values(all) as any[]) {
      for (const t of (ch?.time ?? [])) times.push(new Date(t).getTime());
    }
    const min = times.length ? new Date(Math.min(...times)) : new Date(m.createdAt);
    const max = times.length ? new Date(Math.max(...times)) : new Date();

    const ref = this.dialogService.open(ShmRescheduleDialogComponent, {
      header: this.translateService.instant('measurement.reschedule.window.title'),
      modal: true, closable: true, width: '30vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
      data: {
        min, max,
        current: {
          startTs: m.window?.startTs ? new Date(m.window.startTs) : null,
          endTs: m.window?.endTs ? new Date(m.window.endTs) : null,
        },
      },
    });
    ref.onClose.subscribe((result: { startTs: string | null; endTs: string | null } | undefined) => {
      if (result === undefined) return;
      this.measurementsService.rescheduleWindow(this.measurementId(), result).subscribe(
        this.errorService.handleMutation({
          successMessageKey: 'measurement.reschedule.window.success',
          onSuccess: () => this.getData(),
        }),
      );
    });
  }

  openRawDataViewer(): void {
    const m = this.measurement();
    if (!m) return;
    this.panelService.initialize(RawDataViewerComponent, { measurement: m });
  }

  openDataTrace(): void {
    const m = this.measurement();
    if (!m) return;
    this.dialogService.open(DataTraceDialogComponent, {
      header: this.translateService.instant('measurement.dataTrace.title'),
      modal: true, closable: true, width: '45vw',
      breakpoints: { '960px': '80vw', '640px': '95vw' },
      data: { measurement: m, onChange: () => this.getData() },
    });
  }
}
